import React, {useContext, useState} from "react";
import {AiPromptServiceContext, AppContextContext} from "../../Contexts";
import {Breadcrumb, Button, message, Modal, Space, Table, Tooltip} from "antd";
import {CopyOutlined, DeleteFilled, MinusSquareOutlined, PlusOutlined, PlusSquareOutlined, ReloadOutlined} from "@ant-design/icons";
import {Link, useNavigate} from "react-router-dom";
import * as globalStyles from "../App.module.css";
import AiPrompt from "../../domain/AiPrompt";
import {useTableHandler} from "../../sal-ui/TableHandler";
import {useSearchFilterTableBinding} from "../../sal-ui/TableFilterBinding";
import PagedResult from "../../service/PagedResult";
import {DocumentTitle} from "../DocumentTitle";
import {ColumnsType} from "antd/es/table";
import * as styles from "./AiPromptList.module.css";
import AiPromptConfigKeyTags from "./AiPromptConfigKeyTags";
import {Permission} from "../../domain/auth/Permission";
import {ResourceOwnerType} from "../../domain/ResourceOwnerType";
import {renderOrganizationLink} from "../common/OrganizationLink";
import {ascendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import OwnerFilter, {createDefaultOwnerFilterValues, createOwnerRqbQuery} from "../common/OwnerFilter";
import {normalizeAiPromptFilterValues} from "./AiPromptFilter";
import MarkdownContent from "../common/MarkdownContent";
import MoreLess from "../common/MoreLess";
import {SingleSummaryBlock} from "../common/SummaryBlock";

const PERSISTENT_IDENT = "AiPromptList";
const FILTER_IMMEDIATE_MODE = true;

function AiPromptList() {
    const appContext = useContext(AppContextContext);
    const aiPromptService = useContext(AiPromptServiceContext);
    const navigate = useNavigate();
    const [aiPrompts, setAiPrompts] = useState<PagedResult<AiPrompt>>();
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const title = "AI prompts";
    const hasManage = appContext.user.isSystemAdmin() || appContext.user.hasPermission(Permission.AI_PROMPT__MANAGE_ORG);
    const hasOrgAccess = appContext.user.hasOneOfPermission([Permission.AI_PROMPT__READ_ORG, Permission.AI_PROMPT__MANAGE_ORG]);
    const ownerFilterAccess = {
        canReadSystem: appContext.user.isSystemAdmin() || hasOrgAccess || appContext.user.hasPermission(Permission.AI_PROMPT__READ_SEAL),
        canReadOrganization: appContext.user.isSystemAdmin() || hasOrgAccess
    };
    const defaultFilterValues = createDefaultOwnerFilterValues(appContext, ownerFilterAccess);
    const tableHandler = useTableHandler("name", {
        reloadFunction: reload,
        persistentIdent: PERSISTENT_IDENT
    });
    const filterBinding = useSearchFilterTableBinding({
        persistentIdent: PERSISTENT_IDENT,
        defaultValues: defaultFilterValues,
        normalizeValues: values => normalizeAiPromptFilterValues(values, defaultFilterValues),
        createQuery: createOwnerRqbQuery,
        submitQuery: tableHandler.onRqbSearchSubmit,
        immediateMode: FILTER_IMMEDIATE_MODE
    });
    const columns: ColumnsType<AiPrompt> = [
        {
            dataIndex: "name",
            title: "Name",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            defaultSortOrder: "ascend",
            render: (value, aiPrompt) => <>
                <Link to={`/definitions/ai-prompts/${aiPrompt.id}`}>{value}</Link>
                {aiPrompt.description && <SingleSummaryBlock><MarkdownContent children={aiPrompt.description}/></SingleSummaryBlock>}
                {(aiPrompt.configKeys?.length ?? 0) > 0 &&
                    <div className={styles.configKeys}><AiPromptConfigKeyTags aiPrompt={aiPrompt}/></div>}
            </>
        },
        {
            dataIndex: ["organization", "name"],
            key: "organization.name",
            title: "Owner",
            sorter: true,
            sortDirections: ascendingFirstSortDirections,
            render: (value, aiPrompt) => aiPrompt.owner === ResourceOwnerType.SYSTEM
                ? <i>System</i>
                : renderOrganizationLink(aiPrompt.organization, value)
        }
    ];

    if (hasManage) {
        columns.push({
            title: "Actions",
            width: 100,
            className: globalStyles["table-actions"],
            render: (_, aiPrompt) => <Space>
                <Button icon={<CopyOutlined/>} title="Clone" onClick={() => navigate(`/definitions/ai-prompts/clone/${aiPrompt.id}`)}/>
                {aiPrompt.manageable &&
                    <Tooltip title={aiPrompt.inUse ? "This prompt is in use. See its detail for usage." : "Delete"}>
                        <span>
                            <Button icon={<DeleteFilled/>} disabled={aiPrompt.inUse} aria-label="Delete" onClick={() => {
                                Modal.confirm({
                                    content: <>Do you really want to delete AI prompt <b>{aiPrompt.name}</b>?</>,
                                    okText: "Delete",
                                    cancelText: "Cancel",
                                    okButtonProps: {danger: true},
                                    onOk: () => aiPromptService.delete(aiPrompt).then(() => {
                                        message.success("AI prompt deleted.");

                                        tableHandler.reload();
                                    })
                                });
                            }}/>
                        </span>
                    </Tooltip>}
            </Space>
        });
    }

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]} items={[{title: appContext.config?.appName}, {title}]}/>
                <h1>{title}</h1>
                <OwnerFilter
                    className={styles.filter}
                    values={filterBinding.values}
                    defaultValues={defaultFilterValues}
                    persistentStateIdent={PERSISTENT_IDENT}
                    {...ownerFilterAccess}
                    immediateMode={FILTER_IMMEDIATE_MODE}
                    onChange={values => filterBinding.onChange(normalizeAiPromptFilterValues(values, {ownerIds: []}))}
                />
                <div className={globalStyles["common__top-button-bar"]}>
                    {hasManage && <Button type="primary" icon={<PlusOutlined/>} onClick={() => navigate("/definitions/ai-prompts/add")}>Add</Button>}
                    <Button className="btn-seamless" icon={<ReloadOutlined/>} title="Reload" onClick={tableHandler.reload}/>
                    <Button className="btn-seamless" icon={<PlusSquareOutlined/>} title="Expand all" onClick={() => setExpandedRowKeys(aiPrompts?.data.map(aiPrompt => aiPrompt.id) ?? [])}/>
                    <Button className="btn-seamless" icon={<MinusSquareOutlined/>} title="Collapse all" onClick={() => setExpandedRowKeys([])}/>
                </div>
                <Table
                    dataSource={aiPrompts?.data}
                    loading={tableHandler.loading}
                    pagination={tableHandler.pagination}
                    showSorterTooltip={false}
                    rowKey="id"
                    size="middle"
                    onChange={tableHandler.onTableChange}
                    onRow={aiPrompt => ({onDoubleClick: () => navigate(`/definitions/ai-prompts/${aiPrompt.id}`)})}
                    columns={columns}
                    className={styles.table}
                    expandable={{
                        expandedRowKeys,
                        onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
                        expandedRowRender: aiPrompt => (
                            <section className={styles.promptPreview}>
                                <h2>Prompt</h2>
                                <MoreLess>
                                    <div className={styles.prompt}>{aiPrompt.prompt}</div>
                                </MoreLess>
                            </section>
                        )
                    }}
                />
            </>
        </DocumentTitle>
    );

    function reload() {
        return aiPromptService.getList(tableHandler.queryOptions).then(result => {
            tableHandler.updateTotal(result.total);
            setAiPrompts(result);
        });
    }
}

export default AiPromptList;
